import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Button } from '@devopsafs/react-ui-components';

import Loader from 'src/components/common/loader';
import LocationScreen from 'src/components/common/location-screen';
import LotMap from 'src/components/common/lot-map';
import LabeledValue from './components/labeled-value';
import LotGallery from './components/lot-gallery';
import SchemaHelmet from './components/schema-helmet';
import { ReactComponent as MapPinIcon } from 'src/assets/icons/map-pin.svg';
import { ReactComponent as ShareIcon } from 'src/assets/icons/share.svg';

import globalAppStore from 'src/stores/global-app-store';
import locationScreenStore from 'src/stores/location-screen-store';
import popupStore from 'src/stores/popup-store';
import reservationStore from 'src/stores/reservation-store';

import {
  AppRoute,
  IS_MONUMENTATION_TYPE_SHOW,
  LotField,
  LotTypeShown,
  MonumentationTypeShown,
  PopupName,
  Rights,
  ServicePlace,
} from 'src/constants';
import { useHeader } from 'src/contexts/header-context';
import { useLocalization } from 'src/contexts/localization-context';
import useIsTablet from 'src/hooks/use-is-tablet';
import useWindowSize from 'src/hooks/use-window-size';
import {
  callNativeSharing,
  formatCents,
  getLotDetails,
  getLotFieldLabel,
  getServicePlaceLabel,
} from 'src/utils';

import './lot-page.scss';

const PADDING_TOP = 36;
const DESKTOP_GAP = 32;

const LotPage = observer(() => {
  const { getLocalization } = useLocalization();
  const { id } = useParams();
  const { height } = useWindowSize();
  const [stickyTopPosition, setStickyTopPosition] = useState(PADDING_TOP);
  const stickyBlockRef = useRef<HTMLDivElement | null>(null);
  const { headerHeight } = useHeader();
  const isTablet = useIsTablet();

  useEffect(() => {
    if (id) {
      void reservationStore.init(id);
    }
  }, [id]);

  const { cemetery } = globalAppStore;
  const { isShown: isLocationScreenShown, show } = locationScreenStore;
  const { isLoading, lot } = reservationStore;

  useEffect(() => {
    if (stickyBlockRef.current && lot) {
      const heightDiff = height - stickyBlockRef.current.offsetHeight;

      if (heightDiff < PADDING_TOP + headerHeight) {
        setStickyTopPosition(heightDiff - DESKTOP_GAP - headerHeight);
      } else {
        setStickyTopPosition(PADDING_TOP);
      }
    }
  }, [headerHeight, height, lot, stickyBlockRef]);

  const handleShareButtonClick = useCallback(async () => {
    if (!lot) {
      return;
    }
    const { price, section, type } = lot;
    const shareData = {
      title: `${LotTypeShown[type]}${section ? ` ${section}` : ''} - ${formatCents(price)}`,
      url: window.location.href,
    };
    const sharingText = getLotDetails(lot)
      .map(({ label, field }) => `${label}: ${field}`)
      .join(', ');

    const isNativeSharing = await callNativeSharing({ ...shareData, text: sharingText });
    if (!isNativeSharing) {
      popupStore.showPopup(PopupName.SHARE, shareData);
    }
  }, [lot]);

  if (isLoading) {
    return <Loader className="loader_position_fixed" isDebounce={false} />;
  }

  const {
    bank,
    block,
    description,
    mausoleum,
    monumentationType,
    number,
    photos,
    price,
    rights,
    row,
    section,
    tier,
    type,
  } = lot ?? {};
  const isMausoleum = !!mausoleum;
  const isAllowedMonumentShow = !!monumentationType && IS_MONUMENTATION_TYPE_SHOW;

  if (!cemetery || !lot) {
    return null;
  }

  return (
    <main className="lot-page" id="main">
      <SchemaHelmet lot={lot} cemetery={cemetery} />
      {isLocationScreenShown && (
        <LocationScreen
          coords={lot.coords}
          lotName={`${type ? getLocalization(LotTypeShown[type]) : ''} ${lot.number}`}
        />
      )}
      <div
        className="lot-page__column-wrapper"
        ref={stickyBlockRef}
        style={{ top: stickyTopPosition }}
      >
        <section className="lot-page__info-wrapper" aria-label={getLocalization('Lot details')}>
          <div className="lot-page__info">
            <div className="lot-page__title-wrapper">
              <h1 className="lot-page__title">
                {type ? getLocalization(LotTypeShown[type]) : ''} {number ?? ''}
              </h1>

              <p className="lot-page__price">{price !== undefined ? formatCents(price) : ''}</p>
            </div>

            {description ? <p className="lot-page__description">{description}</p> : null}
          </div>

          <div className="lot-page__location-section">
            <h2 className="lot-page__sub-title lot-page__sub-title_location">
              {getLocalization('Location')}
            </h2>

            <div className="lot-page__location-content">
              <div className="lot-page__location-row">
                <LabeledValue
                  label={getLocalization('Cemetery')}
                  value={globalAppStore.cemeteryInfo.name}
                />
              </div>

              {isMausoleum ? (
                <div className="lot-page__location-row">
                  <LabeledValue
                    className="lot-page__labeled-value"
                    label={getLocalization(getServicePlaceLabel(lot.type, ServicePlace.MAUSOLEUM))}
                    value={mausoleum.name}
                  />
                </div>
              ) : null}

              <div className="lot-page__location-row lot-page__location-row_multiple">
                {isMausoleum ? (
                  <>
                    <LabeledValue
                      className="lot-page__labeled-value"
                      label={getLocalization(getLotFieldLabel(lot.type, LotField.BANK))}
                      value={bank}
                    />

                    <LabeledValue
                      className="lot-page__labeled-value"
                      label={getLocalization(getLotFieldLabel(lot.type, LotField.TIER))}
                      value={tier}
                    />

                    <LabeledValue
                      className="lot-page__labeled-value"
                      label={getLocalization(getLotFieldLabel(lot.type, LotField.ROW))}
                      value={row}
                    />
                  </>
                ) : (
                  <>
                    <LabeledValue
                      className="lot-page__labeled-value"
                      label={getLocalization(getServicePlaceLabel(lot.type, ServicePlace.SECTION))}
                      value={section}
                    />

                    <LabeledValue
                      className="lot-page__labeled-value"
                      label={getLocalization(getLotFieldLabel(lot.type, LotField.BLOCK))}
                      value={block}
                    />
                  </>
                )}

                <LabeledValue
                  className="lot-page__labeled-value"
                  label={getLocalization(getLotFieldLabel(lot.type, LotField.RIGHTS))}
                  value={rights ? getLocalization(Rights[rights]) : ''}
                />
              </div>

              {isAllowedMonumentShow ? (
                <div className="lot-page__location-row">
                  <LabeledValue
                    className="lot-page__labeled-value"
                    label={getLocalization(getLotFieldLabel(lot.type, LotField.MONUMENTATION_TYPE))}
                    value={getLocalization(MonumentationTypeShown[monumentationType])}
                  />
                </div>
              ) : null}
            </div>

            <div className="lot-page__buttons-wrapper">
              <Button
                className="lot-page__button"
                iconPosition="center"
                icon={<MapPinIcon />}
                label={isTablet ? getLocalization('Show on Map') : getLocalization('Show Map')}
                onClick={show}
                tag="button"
                theme="ghost"
              />

              <Button
                className="lot-page__button"
                iconPosition="center"
                icon={<ShareIcon />}
                label={isTablet ? getLocalization('Share Lot') : getLocalization('Share')}
                onClick={handleShareButtonClick}
                theme="ghost"
              />
            </div>
          </div>
        </section>

        <section
          className="lot-page__reservation-section"
          aria-label={getLocalization('Lot Reservation')}
        >
          <div className="lot-page__heading">
            <h2 className="lot-page__sub-title">{getLocalization('Lot Reservation')}</h2>

            <p className="lot-page__reservation-text">
              {getLocalization(
                'Send your request for a lot reservation. We will process your request within one business day.'
              )}
            </p>
          </div>

          <Button
            className="lot-page__reservation-button"
            href={`${AppRoute.RESERVATION_PAGE}/${id}`}
            label={getLocalization('Request')}
            tag="link"
            target="_blank"
            theme="filled"
            size="oversize"
          />
        </section>
      </div>

      <div className="lot-page__gallery">
        {photos?.length ? <LotGallery listImages={photos} /> : null}

        <div className="lot-page__map-wrapper">
          <LotMap
            className="lot-page__map"
            cemetery={cemetery}
            coords={lot.coords}
            height={isTablet ? '440px' : '320px'}
            width="100%"
            isFullScreen={isLocationScreenShown}
            handleFullScreenButtonClick={show}
          />
        </div>
      </div>
    </main>
  );
});

export default LotPage;
